import React, { useState } from 'react';
import {
	groupBy,
	sortBy,
	filter,
	map,
	find,
	get,
} from 'lodash';
import { Redirect } from 'react-router-dom';
import { getPathByRoute } from 'App.js';
import routeKeys from 'CustomerRouteKeys.js';
import { useTransitAccountIdContext } from 'context/TransitAccountIdContext.js';
import { useTransitAccount } from "components/data/transit-account/TransitAccount.js";
import { Error as ComponentError } from 'components/data/Error.js';
import { useTransitAccountProductCatalog } from "components/data/transit-account/ProductCatalog.query.js";
import { getGroupedModes } from 'components/icons/TransitModes.js';
import { ComponentLoading } from 'components/icons/LoadingIcon.js';
import { WSAUTOLOAD_TYPES } from 'server/api-types/WSAutoloadFactory.js';
import { useModalContext } from "context/ModalProvider.js";
import ProductItem from 'components/account/ProductItem.js';
import { useGetSubsystemProductCatalog } from 'components/data/subsystem/ProductCatalog.query.js';

import * as plist from './ProductList.module.css';


const ProductList = () => {
	const [ redirect, setRedirect ] = useState(null);
	const transit_account_id = useTransitAccountIdContext();
	const { setModal } = useModalContext();

	const onModalClose = () => setModal(null);

	const {
		loading: transitAccountLoading,
		data: transitAccountQdata,
		error: transitAccountError,
	} = useTransitAccount({
		subsystemAccountReference: transit_account_id,
		returnSubscriptions: true,
	});

	const {
		products: subsystemProducts,
		operators,
		loading: productCatalogLoading,
		error: productCatalogError,
	} = useGetSubsystemProductCatalog({ riderClassId: get(transitAccountQdata?.transitAccountQ, 'riderClass') });

	const {
		products: transitAccountProducts,
		queryResult: {
			loading: transitAccountCatalogLoading,
			errors: transitAccountCatalogError,
		},
	} = useTransitAccountProductCatalog({ subsystemAccountReference: transit_account_id });

	if (transitAccountError || productCatalogError || transitAccountCatalogError) {
		return <ComponentError error={transitAccountError} />;
	}

	if (transitAccountLoading || productCatalogLoading || transitAccountCatalogLoading) {
		return <ComponentLoading />;
	}
	const handleEditAutoload = ({ wsAutoloadSubscriptionInfo }) => {
		setRedirect(<Redirect push to={{
			pathname: getPathByRoute(routeKeys.AutoloadPass, {
				transit_account_id,
				product_sku: wsAutoloadSubscriptionInfo.autoload.productSKU,
			}),
			state: { wsAutoloadSubscriptionInfo, updateAutoload: true },
		}} />);
	};

	const subscriptions = get(transitAccountQdata?.transitAccountQ, 'subscriptions');
	const autoLoadPasses = filter(subscriptions ?? [], wsAutoloadSummary => wsAutoloadSummary.autoload.type === WSAUTOLOAD_TYPES.purchaseSubsystemProduct);
	const groupedPasses = groupBy(transitAccountQdata?.transitAccountQ.passes, 'passSKU');

	if (redirect) {
		return redirect;
	}

	return (
		<div className={plist.container}>
			{map(groupedPasses, (wsSubsystemAccountPassGroup, passSKU) => {
				const wsAccountProduct = find(subsystemProducts, { productSKU: passSKU });
				const isInSubsystemCatalog = Boolean(wsAccountProduct);
				const isInTransitAccountCatalog = Boolean(find(transitAccountProducts, { productSku: passSKU }));
				const groupedModes = (wsAccountProduct?.permittedUsers && wsAccountProduct.permittedUsers?.length)
					? getGroupedModes(wsAccountProduct, operators)
					: [];

				const wsAutoloadSummary = find(
					autoLoadPasses ?? [],
					wsAutoloadSummary => wsAutoloadSummary?.autoload?.productSKU === passSKU
				);

				const sortedWsSubsystemAccountPassGroup = sortBy(
					wsSubsystemAccountPassGroup,
					[
						'startDateTime',
						'endDateTime',
						'createdDateTime',
					]
				);

				// If we can not find the product in the catalog then the user can't reload it so we set maxAddPass to zero
				const maxAddPass = isInTransitAccountCatalog
					? transitAccountProducts.find(product => product.productSku === passSKU)?.maxAddPass ?? 0
					: subsystemProducts.find(product => product.productSKU === passSKU)?.maxAddPass ?? 0;

				return (
					<div className={plist.product} key={`${passSKU}`}>
						<ProductItem {...{
							groupedModes,
							wsSubsystemAccountPassGroup: sortedWsSubsystemAccountPassGroup,
							wsAutoloadSummary,
							maxAddPass,
							onModalClose,
							setModal,
							handleEditAutoload,
							isInSubsystemCatalog,
							isInTransitAccountCatalog,
						}} />
					</div>
				);
			})}
		</div>
	);
};

export default ProductList;
